import { useState, useEffect, useCallback } from 'react';
import { Clock, AlertTriangle, CheckCircle, XCircle, ShieldOff, Users, Link2, KeyRound, Filter } from 'lucide-react';
import AdminNav from '../components/AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  expiring_soon: { label: 'Expiring Soon', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-400', icon: AlertTriangle, barColor: 'bg-amber-500' },
  active: { label: 'Active', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-400', icon: CheckCircle, barColor: 'bg-emerald-500' },
  expired: { label: 'Expired', color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', ring: 'ring-slate-300', icon: Clock, barColor: 'bg-slate-300' },
  exhausted: { label: 'Exhausted', color: 'text-orange-500', bg: 'bg-orange-50', border: 'border-orange-200', ring: 'ring-orange-400', icon: XCircle, barColor: 'bg-orange-400' },
  revoked: { label: 'Revoked', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', ring: 'ring-red-400', icon: ShieldOff, barColor: 'bg-red-400' },
};

const TYPE_CONFIG = {
  demo_link: { label: 'Demo Link', icon: Link2, color: 'text-blue-600', bg: 'bg-blue-50' },
  access_token: { label: 'Access Token', icon: KeyRound, color: 'text-violet-600', bg: 'bg-violet-50' },
  guest_access: { label: 'Guest Access', icon: Users, color: 'text-teal-600', bg: 'bg-teal-50' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDaysRemaining = (days) => {
  if (days === null || days === undefined) return '';
  if (days < 0) return `${Math.abs(Math.round(days))}d ago`;
  if (days < 1) return `${Math.round(days * 24)}h left`;
  return `${Math.round(days)}d left`;
};

const LinkExpiryDashboardPage = () => {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/link-expiry/dashboard`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load expiry data');
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredItems = data.items.filter(item => {
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    return true;
  });

  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="link-expiry-dashboard">
      <AdminNav />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-900" data-testid="expiry-dashboard-title">Link Expiry Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Visual timeline of all demo links, access tokens, and guest credentials</p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="expiry-error">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="expiry-summary-cards">
          {[
            { key: 'expiring_soon', label: 'Expiring Soon', value: s.expiring_soon || 0, icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
            { key: 'active', label: 'Active', value: s.active || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
            { key: 'expired', label: 'Expired', value: s.expired || 0, icon: Clock, color: 'text-slate-400', bg: 'bg-slate-100', border: 'border-slate-200' },
            { key: 'exhausted', label: 'Exhausted', value: s.exhausted || 0, icon: XCircle, color: 'text-orange-500', bg: 'bg-orange-50', border: 'border-orange-200' },
            { key: 'revoked', label: 'Revoked', value: s.revoked || 0, icon: ShieldOff, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
          ].map(card => {
            const Icon = card.icon;
            return (
              <button
                key={card.key}
                onClick={() => setStatusFilter(statusFilter === card.key ? 'all' : card.key)}
                data-testid={`summary-card-${card.key}`}
                className={`relative rounded-xl border p-4 text-left transition-all ${
                  statusFilter === card.key ? `${card.border} ${card.bg} ring-2 ring-offset-1 ring-opacity-40` : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${card.color}`} />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{card.label}</span>
                </div>
                <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4" data-testid="expiry-filters">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex gap-1">
            {[
              { key: 'all', label: 'All Types' },
              { key: 'demo_link', label: 'Demo Links' },
              { key: 'access_token', label: 'Access Tokens' },
              { key: 'guest_access', label: 'Guest Access' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setTypeFilter(f.key)}
                data-testid={`type-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  typeFilter === f.key
                    ? 'bg-[#0077FF] text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {(typeFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => { setTypeFilter('all'); setStatusFilter('all'); }}
              data-testid="clear-filters-btn"
              className="text-xs text-slate-400 hover:text-slate-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Timeline List */}
        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="expiry-loading">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="expiry-empty">
            <p className="text-slate-400 text-sm">No items match the current filters</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="expiry-list">
            {filteredItems.map((item) => {
              const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.active;
              const tc = TYPE_CONFIG[item.type] || TYPE_CONFIG.access_token;
              const StatusIcon = sc.icon;
              const TypeIcon = tc.icon;

              return (
                <div
                  key={`${item.type}-${item.id}`}
                  data-testid={`expiry-row-${item.id}`}
                  className={`bg-white border rounded-xl overflow-hidden transition-all hover:shadow-sm ${
                    item.status === 'expiring_soon' ? 'border-amber-200' : 'border-slate-200'
                  }`}
                >
                  <div className="flex">
                    {/* Status indicator bar */}
                    <div className={`w-1 shrink-0 ${sc.barColor}`} />

                    <div className="flex-1 px-5 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <p className="font-semibold text-slate-800 text-sm truncate">{item.label}</p>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc.bg} ${sc.color}`}>
                              <StatusIcon className="w-2.5 h-2.5" />
                              {sc.label}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${tc.bg} ${tc.color}`}>
                              <TypeIcon className="w-2.5 h-2.5" />
                              {tc.label}
                            </span>
                          </div>

                          {item.experience_name && (
                            <p className="text-xs text-slate-400 mb-2">{item.experience_name}</p>
                          )}

                          {/* Expiry timeline bar */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 max-w-xs">
                              <ExpiryBar daysRemaining={item.days_remaining} status={item.status} />
                            </div>
                            <div className="flex items-center gap-4 text-[11px] text-slate-500 shrink-0">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {item.expires_at ? formatDate(item.expires_at) : 'No expiry'}
                              </span>
                              {item.days_remaining !== null && item.days_remaining !== undefined && (
                                <span className={`font-semibold ${
                                  item.status === 'expiring_soon' ? 'text-amber-600' :
                                  item.status === 'expired' ? 'text-slate-400' :
                                  'text-slate-600'
                                }`}>
                                  {formatDaysRemaining(item.days_remaining)}
                                </span>
                              )}
                              {item.login_count !== null && item.login_count !== undefined && (
                                <span>Logins: {item.login_count}/{item.max_logins}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* Visual expiry progress bar */
const ExpiryBar = ({ daysRemaining, status }) => {
  let pct = 100;
  if (daysRemaining !== null && daysRemaining !== undefined) {
    // Show as percentage of 30-day window
    pct = Math.max(0, Math.min(100, (daysRemaining / 30) * 100));
  }
  if (status === 'expired' || status === 'revoked') pct = 0;

  const barColor =
    status === 'expiring_soon' ? 'bg-amber-500' :
    status === 'active' ? 'bg-emerald-500' :
    status === 'exhausted' ? 'bg-orange-400' :
    'bg-slate-300';

  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="expiry-bar">
      <div
        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

export default LinkExpiryDashboardPage;
